import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2Icon,
  FileTextIcon,
  GavelIcon,
  HandshakeIcon,
  InboxIcon,
  ReceiptIcon,
  SearchIcon,
  SparklesIcon,
  TrendingUpIcon,
  UserRoundIcon,
  type LucideIcon
} from 'lucide-react';
import { getSearchSiteQueryKey, useSearchSite, type SearchSiteParams, type SiteSearchResult } from '@cdf/api-client-react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { useOptionalWorkspace } from '@/components/workspace/workspace-provider';
import { getMainNavigation, type NavSection } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { useAiAssistant } from './ai-context';

export type RecordKind = SiteSearchResult['type'];
export type SearchKind = RecordKind | 'page';
/** Result refiner: everything, or one record type. */
export type SearchRefiner = 'all' | SearchKind;
type EntryKind = SearchKind | 'action';

export interface PaletteEntry {
  id: string;
  kind: EntryKind;
  label: string;
  hint?: string;
  run: () => void;
}

export const SEARCH_KIND_META: Record<EntryKind, { label: string; plural: string; icon: LucideIcon }> = {
  account: { label: 'Account', plural: 'Accounts', icon: Building2Icon },
  contact: { label: 'Contact', plural: 'Contacts', icon: UserRoundIcon },
  opportunity: { label: 'Opportunity', plural: 'Opportunities', icon: TrendingUpIcon },
  enquiry: { label: 'Enquiry', plural: 'Enquiries', icon: InboxIcon },
  tender: { label: 'Tender', plural: 'Tenders', icon: GavelIcon },
  quote: { label: 'Quote', plural: 'Quotes', icon: ReceiptIcon },
  agreement: { label: 'Agreement', plural: 'Agreements', icon: HandshakeIcon },
  page: { label: 'Page', plural: 'Pages', icon: FileTextIcon },
  action: { label: 'Ask AI', plural: 'Ask AI', icon: SparklesIcon }
};

/** Display order of record groups; accounts and contacts come first. */
export const RECORD_KINDS: readonly RecordKind[] = ['account', 'contact', 'opportunity', 'enquiry', 'tender', 'quote', 'agreement'];
/** Refiners shown even before their counts are known. */
const ALWAYS_SHOWN: ReadonlySet<RecordKind> = new Set(['account', 'contact']);

/** Browser event the header search box uses to open the palette. */
export const OPEN_COMMAND_PALETTE_EVENT = 'cdf:open-command-palette';

export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE_EVENT));
}

/** Flattens menu sections into page entries the person may open. */
export function paletteNavigationPages(sections: readonly NavSection[], hasPermission: (key: string) => boolean) {
  const pages: Array<{ title: string; to: string; section: string }> = [];
  for (const section of sections) {
    for (const item of section.items) {
      if (item.permissions && !item.permissions.some(hasPermission)) continue;
      if (item.to && !item.external && !item.to.includes(':')) pages.push({ title: item.title, to: item.to, section: section.heading });
      for (const child of item.children ?? []) pages.push({ title: child.title, to: child.to, section: `${section.heading} · ${item.title}` });
    }
  }
  return pages;
}

/** Case-insensitive match on every word, so "acc rev" finds "Account reviews". */
export function matchesQuery(text: string, query: string): boolean {
  const haystack = text.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every(word => haystack.includes(word));
}

/** Splits text into plain and matched parts for highlighting search words. */
export function highlightParts(text: string, query: string): Array<{ text: string; match: boolean }> {
  const words = [...new Set(query.toLowerCase().split(/\s+/).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (words.length === 0) return [{ text, match: false }];
  const escaped = words.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  return text.split(pattern).filter(Boolean).map(part => ({ text: part, match: words.includes(part.toLowerCase()) }));
}

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, index) =>
        part.match ? <mark key={index} className="text-foreground bg-transparent font-semibold">{part.text}</mark> : <React.Fragment key={index}>{part.text}</React.Fragment>
      )}
    </>
  );
}

/** Results per type in the "All" view, and when one type is selected. */
const ALL_LIMIT = 5;
const REFINED_LIMIT = 20;

/** ⌘K search across the site: records ranked with accounts and contacts first, plus pages. */
export function CommandPalette() {
  const navigate = useNavigate();
  const { hasPermission, enabledModules, status } = useTenantEntity();
  const mode = useOptionalWorkspace()?.mode ?? 'all';
  const assistant = useAiAssistant();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [refiner, setRefiner] = React.useState<SearchRefiner>('all');
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(value => !value);
      }
    };
    const openHandler = () => setOpen(true);
    window.addEventListener('keydown', handler);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, openHandler);
    };
  }, []);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  const canRead = status === 'ready' && hasPermission('entity.records.read');
  const search = debounced.length >= 2 && open && canRead;
  const recordRefiner = refiner !== 'all' && refiner !== 'page' ? refiner : null;
  const params: SearchSiteParams = recordRefiner
    ? { search: debounced, types: recordRefiner, limit: REFINED_LIMIT, offset: 0 }
    : { search: debounced, limit: ALL_LIMIT, offset: 0 };
  const results = useSearchSite(params, {
    query: { queryKey: getSearchSiteQueryKey(params), enabled: search && refiner !== 'page', staleTime: 15_000 }
  });

  const close = React.useCallback(() => { setOpen(false); setQuery(''); setRefiner('all'); setActive(0); }, []);
  const go = React.useCallback((to: string) => { close(); navigate(to); }, [close, navigate]);

  const text = query.trim();
  const matchedPages = React.useMemo(() => {
    if (!text) return [];
    const seen = new Set<string>();
    return paletteNavigationPages(getMainNavigation(mode, enabledModules), hasPermission).filter(page => {
      if (seen.has(page.to) || !matchesQuery(`${page.title} ${page.section}`, text)) return false;
      seen.add(page.to);
      return true;
    });
  }, [text, mode, enabledModules, hasPermission]);

  const data = search ? results.data : undefined;
  const counts = data?.counts;
  const totals: Record<SearchKind, number | undefined> = {
    account: counts?.account,
    contact: counts?.contact,
    opportunity: counts?.opportunity,
    enquiry: counts?.enquiry,
    tender: counts?.tender,
    quote: counts?.quote,
    agreement: counts?.agreement,
    page: matchedPages.length
  };

  const entries = React.useMemo<PaletteEntry[]>(() => {
    if (!text) return [];
    const list: PaletteEntry[] = [];
    if (refiner !== 'page') {
      const items = (data?.items ?? []).filter(item => !recordRefiner || item.type === recordRefiner);
      for (const kind of RECORD_KINDS) {
        for (const item of items.filter(result => result.type === kind)) {
          const hint = [item.subtitle, item.type === 'account' ? null : item.accountName].filter(Boolean).join(' · ');
          list.push({ id: item.key, kind: item.type, label: item.title, hint: hint || undefined, run: () => go(item.deepLink) });
        }
      }
    }
    if (refiner === 'all' || refiner === 'page') {
      for (const page of matchedPages.slice(0, refiner === 'page' ? REFINED_LIMIT : ALL_LIMIT)) {
        list.push({ id: `page:${page.to}`, kind: 'page', label: page.title, hint: page.section, run: () => go(page.to) });
      }
    }
    if (refiner === 'all') {
      list.push({
        id: 'action:ask-ai', kind: 'action', label: `Ask AI: “${text}”`, hint: '⌘J',
        run: () => { close(); assistant.ask(text); }
      });
    }
    return list;
  }, [text, refiner, recordRefiner, data, matchedPages, go, close, assistant]);

  React.useEffect(() => { setActive(0); }, [query, refiner]);

  const loadingRecords = search && refiner !== 'page' && results.isFetching;
  const recordRefiners = canRead
    ? RECORD_KINDS.filter(kind => ALWAYS_SHOWN.has(kind) || (totals[kind] ?? 0) > 0 || refiner === kind)
    : [];
  const refiners: SearchRefiner[] = ['all', ...recordRefiners, 'page'];
  const allTotal = counts ? RECORD_KINDS.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0) + matchedPages.length : undefined;
  const hasResults = entries.some(entry => entry.kind !== 'action');
  const waitingForRecords = canRead && text.length >= 2 && debounced !== text;

  const chooseRefiner = (next: SearchRefiner) => {
    setRefiner(next);
    inputRef.current?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={next => (next ? setOpen(true) : close())}>
      <DialogContent className="top-[12%] max-w-2xl translate-y-0 gap-0 p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>Search the whole site. Filter by type, then use the arrow keys and Enter.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b px-3">
          <SearchIcon className="text-muted-foreground size-4" aria-hidden />
          <Input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search"
            aria-label="Search"
            className="h-12 border-0 text-base shadow-none focus-visible:ring-0"
            role="combobox"
            aria-expanded
            aria-controls="command-palette-results"
            aria-activedescendant={entries[active] ? `palette-${entries[active].id}` : undefined}
            onKeyDown={event => {
              if (event.key === 'ArrowDown') { event.preventDefault(); setActive(value => Math.min(entries.length - 1, value + 1)); }
              else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(value => Math.max(0, value - 1)); }
              else if (event.key === 'Enter') { event.preventDefault(); entries[active]?.run(); }
            }}
          />
        </div>

        {text ? (
          <div role="group" aria-label="Filter results by type" className="flex flex-wrap gap-1.5 border-b px-3 py-2">
            {refiners.map(option => {
              const label = option === 'all' ? 'All' : SEARCH_KIND_META[option].plural;
              const count = option === 'all' ? allTotal : totals[option];
              const selected = refiner === option;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => chooseRefiner(option)}
                  className={cn(
                    'focus-visible:ring-ring inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                    selected ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted text-foreground'
                  )}
                >
                  {label}
                  {count !== undefined ? <span className={cn('tabular-nums', selected ? 'opacity-80' : 'text-muted-foreground')}>{count}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}

        <ul id="command-palette-results" role="listbox" aria-label="Results" className="max-h-[60dvh] overflow-y-auto p-2">
          {!text ? (
            <li role="presentation" className="text-muted-foreground px-2 py-6 text-center text-sm">
              {canRead
                ? 'Search accounts, contacts, opportunities, enquiries, tenders, quotes, agreements and pages.'
                : 'Search pages.'}
            </li>
          ) : null}
          {entries.map((entry, index) => {
            const meta = SEARCH_KIND_META[entry.kind];
            const Icon = meta.icon;
            const showGroup = index === 0 || entries[index - 1]!.kind !== entry.kind;
            const total = entry.kind === 'action' ? undefined : totals[entry.kind];
            const shown = entries.filter(item => item.kind === entry.kind).length;
            return (
              <React.Fragment key={entry.id}>
                {showGroup && entry.kind !== 'action' ? (
                  <li role="presentation" className="text-muted-foreground flex items-center justify-between px-2 pt-2 pb-1 text-xs font-medium">
                    <span>{meta.plural}</span>
                    {total !== undefined && total > shown ? (
                      refiner === 'all' ? (
                        <button type="button" className="hover:text-foreground underline-offset-2 hover:underline" onClick={() => chooseRefiner(entry.kind as SearchKind)}>
                          Show all {total}
                        </button>
                      ) : (
                        <span>Showing {shown} of {total}. Keep typing to narrow down.</span>
                      )
                    ) : null}
                  </li>
                ) : null}
                {showGroup && entry.kind === 'action' && index > 0 ? <li role="presentation" className="my-1 border-t" /> : null}
                <li
                  id={`palette-${entry.id}`}
                  role="option"
                  aria-selected={index === active}
                  className={cn('flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm', index === active ? 'bg-accent' : 'hover:bg-muted')}
                  onMouseEnter={() => setActive(index)}
                  onClick={entry.run}
                >
                  <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{entry.kind === 'action' ? entry.label : <Highlighted text={entry.label} query={text} />}</span>
                    {entry.hint ? <span className="text-muted-foreground block truncate text-xs">{entry.hint}</span> : null}
                  </span>
                  <span className="text-muted-foreground shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium" data-testid="result-type">
                    {meta.label}
                  </span>
                </li>
              </React.Fragment>
            );
          })}
          {loadingRecords || waitingForRecords ? <li role="status" className="text-muted-foreground px-2 py-2 text-xs">Searching…</li> : null}
          {text && !loadingRecords && !waitingForRecords && !hasResults ? (
            <li role="presentation" className="text-muted-foreground px-2 py-2 text-xs">
              {refiner === 'all' ? 'Nothing matches. Try fewer or different words.' : `No matching ${SEARCH_KIND_META[refiner].plural.toLowerCase()}.`}
            </li>
          ) : null}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
